// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button, Card, Input, Label, Stack } from '@intakeform/ui';
import { ALL_FINDING_CODES } from '@intakeform/validation';
import { useMe } from '../../api/auth.js';
import { usePatchOrg } from '../../api/customer-enablement.js';
import { EmrConnectionPanel } from './EmrConnectionPanel.js';
import { EligibilityConnectionPanel } from './EligibilityConnectionPanel.js';
import { ApiKeysPanel } from './ApiKeysPanel.js';
import { WebhooksPanel } from './WebhooksPanel.js';

interface OrgSettingsShape {
  freshness_days?: number;
  brand_name?: string;
  brand_color?: string;
  clinical_significance_codes?: string[];
  magic_link_ttl_seconds?: number;
}

const DEFAULT_CLINICAL_CODES = new Set(['contradiction', 'eligibility', 'stale']);

export function OrgSettingsRoute() {
  const { data: me } = useMe();
  const patch = usePatchOrg();

  const [name, setName] = useState('');
  const [freshness, setFreshness] = useState('');
  const [brandName, setBrandName] = useState('');
  const [brandColor, setBrandColor] = useState('');
  /** Per-org default magic-link TTL, displayed in HOURS for clinician
   *  ergonomics but persisted as seconds. Empty string = "use env
   *  default" (fall through). */
  const [linkTtlHours, setLinkTtlHours] = useState('');
  // null = "use engine default"; Set<string> = explicit per-org override
  // (including the empty set, which means "nothing flags").
  const [clinicalCodes, setClinicalCodes] = useState<Set<string> | null>(null);
  /** Astrum organization id — what SSO provisions new staff against (200.3.1). */
  const [astrumOrgId, setAstrumOrgId] = useState('');

  useEffect(() => {
    if (!me) return;
    setName(me.organization.name);
    setAstrumOrgId(me.organization.externalOrgId ?? '');
    const settings = (me.organization.settings as OrgSettingsShape | undefined) ?? {};
    if (typeof settings.freshness_days === 'number') setFreshness(String(settings.freshness_days));
    if (typeof settings.brand_name === 'string') setBrandName(settings.brand_name);
    if (typeof settings.brand_color === 'string') setBrandColor(settings.brand_color);
    if (typeof settings.magic_link_ttl_seconds === 'number') {
      setLinkTtlHours(String(settings.magic_link_ttl_seconds / 3600));
    }
    if (Array.isArray(settings.clinical_significance_codes)) {
      setClinicalCodes(new Set(settings.clinical_significance_codes));
    } else {
      setClinicalCodes(null);
    }
  }, [me]);

  if (!me) return null;
  if (me.user.role !== 'admin') {
    return (
      <Card heading="Organization Settings" tone="danger">
        <p>Only administrators can change clinic settings.</p>
        <p style={{ marginTop: 12 }}>
          <Link to="/dashboard">Back to dashboard</Link>
        </p>
      </Card>
    );
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    // Starts EMPTY on purpose (intakeform-preview-w0rm). Every key below is
    // added only when the admin actually supplied a value, so saving this
    // form to change one field cannot quietly persist another. It used to be
    // seeded with `magic_link_sender: sender` — a control nothing read, whose
    // state defaulted to "console" — so an admin editing the link expiry
    // directly above it silently wrote "print patient login tokens to the API
    // log" into their clinic's settings row.
    const settings: OrgSettingsShape = {};
    if (freshness.trim().length > 0) {
      const n = Number(freshness);
      if (Number.isFinite(n) && n > 0) settings.freshness_days = Math.floor(n);
    }
    if (brandName.trim().length > 0) settings.brand_name = brandName.trim();
    if (brandColor.trim().length > 0) settings.brand_color = brandColor.trim();
    if (linkTtlHours.trim().length > 0) {
      const h = Number(linkTtlHours);
      if (Number.isFinite(h) && h > 0) settings.magic_link_ttl_seconds = Math.round(h * 3600);
    }
    if (clinicalCodes !== null) {
      settings.clinical_significance_codes = Array.from(clinicalCodes);
    }
    // Empty box means "not linked": send null to clear it rather than omitting
    // the key, which would leave a stale link in place after an admin deleted
    // the value and saved.
    const trimmedOrgId = astrumOrgId.trim();
    patch.mutate({
      name: name.trim(),
      settings,
      astrumOrgId: trimmedOrgId.length > 0 ? trimmedOrgId : null,
    });
  };

  const toggleClinicalCode = (code: string): void => {
    // Promote from "default" to "explicit override" the moment the
    // clinician touches any checkbox — locking the current set as
    // the baseline before the toggle takes effect.
    const next = new Set(clinicalCodes ?? DEFAULT_CLINICAL_CODES);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setClinicalCodes(next);
  };

  const resetClinicalToDefault = (): void => setClinicalCodes(null);
  const activeClinicalCodes = clinicalCodes ?? DEFAULT_CLINICAL_CODES;
  const usingDefault = clinicalCodes === null;

  return (
    <Stack gap={16}>
      <Card heading="Clinic Settings" variant="default">
        <form onSubmit={onSubmit} style={{ maxWidth: 560, marginTop: 12 }}>
          <Stack gap={16}>
            <Stack gap={4}>
              <Label htmlFor="org-name" required>
                Clinic name
              </Label>
              <Input
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                required
              />
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="org-astrum-org-id">Astrum organization id</Label>
              <Input
                id="org-astrum-org-id"
                type="text"
                value={astrumOrgId}
                onChange={(e) => setAstrumOrgId(e.currentTarget.value)}
                maxLength={200}
                placeholder="Leave blank if this clinic does not use Astrum SSO"
              />
              <p className="text-xs text-muted-foreground">
                Links this clinic to its Astrum organization, so staff signing in with Astrum are
                placed here. Use the organization id (<code>astrum_org_id</code>), not a practice id
                — a practice is one part of an organization. Clearing this box unlinks it.
              </p>
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="org-freshness">Freshness threshold (days)</Label>
              <Input
                id="org-freshness"
                type="number"
                min={1}
                max={3650}
                value={freshness}
                onChange={(e) => setFreshness(e.currentTarget.value)}
                aria-describedby="org-freshness-hint"
              />
              <span id="org-freshness-hint" className="text-xs text-muted-foreground">
                Answers older than this are flagged as stale on submission.
              </span>
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="org-brand-name">Brand name (optional)</Label>
              <Input
                id="org-brand-name"
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.currentTarget.value)}
              />
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="org-brand-color">Brand colour (optional, #RRGGBB)</Label>
              <Input
                id="org-brand-color"
                type="text"
                pattern="^#[0-9a-fA-F]{6}$"
                value={brandColor}
                onChange={(e) => setBrandColor(e.currentTarget.value)}
                placeholder="#1d4ed8"
              />
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="org-link-ttl">Magic-link expiry (hours)</Label>
              <Input
                id="org-link-ttl"
                data-testid="org-link-ttl"
                type="number"
                min={0.0167}
                max={720}
                // step="any", NOT a fixed step (intakeform-preview-271b). A
                // number input's step base is `min`, so step=0.25 with
                // min=0.0167 made the only valid entries 0.0167 + 0.25n —
                // which rejects 0.25, 2 and 24 alike. An org already on the
                // 900s default hydrates this box to "0.25", so constraint
                // validation blocked submit and NO field on this form could
                // be saved until the box was cleared.
                step="any"
                value={linkTtlHours}
                onChange={(e) => setLinkTtlHours(e.currentTarget.value)}
                placeholder="leave blank to use server default"
                aria-describedby="org-link-ttl-hint"
              />
              <span id="org-link-ttl-hint" className="text-xs text-muted-foreground">
                Default link lifetime for this clinic. Forms can override per-form; admin can
                override per-link at issuance. Bounds: 1 minute — 30 days. Leave blank to use the
                server-wide default.
              </span>
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="org-clinical-codes">Clinically-significant finding codes</Label>
              <p className="text-xs text-muted-foreground" style={{ margin: 0 }}>
                Submissions trip the Flagged badge when validation produces a finding with one of
                these codes. Default is <code>contradiction</code>, <code>eligibility</code>,{' '}
                <code>stale</code>. Tighten or loosen for your clinic.
                {usingDefault ? ' (currently using the default set)' : ''}
              </p>
              <div
                id="org-clinical-codes"
                data-testid="clinical-codes-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '4px 16px',
                  padding: '12px',
                  border: '1px solid var(--color-border, #e5e7eb)',
                  borderRadius: 6,
                }}
              >
                {ALL_FINDING_CODES.map((code) => (
                  <label
                    key={code}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid={`clinical-code-${code}`}
                      checked={activeClinicalCodes.has(code)}
                      onChange={() => toggleClinicalCode(code)}
                    />
                    <code>{code}</code>
                  </label>
                ))}
              </div>
              {!usingDefault ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetClinicalToDefault}
                  data-testid="clinical-codes-reset"
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  Reset to default
                </Button>
              ) : null}
            </Stack>

            {patch.error ? (
              <div className="error-banner text-sm" role="alert" aria-live="polite">
                {patch.error.message}
              </div>
            ) : null}

            {patch.isSuccess ? (
              <div role="status" aria-live="polite" className="text-sm text-muted-foreground">
                Settings saved.
              </div>
            ) : null}

            <Button type="submit" variant="primary" disabled={patch.isPending}>
              {patch.isPending ? 'Saving…' : 'Save settings'}
            </Button>
          </Stack>
        </form>
      </Card>
      <EmrConnectionPanel />
      <EligibilityConnectionPanel />
      <ApiKeysPanel />
      <WebhooksPanel />
    </Stack>
  );
}
